/** Local, deterministic gain processing. These settings never enter a provider request. */
export const AUDIO_GAIN_LEVELS=[-12,-6,0,3,6] as const;
export const VOLUME_MOTIONS=['Consistent','Rising','Falling','Varied'] as const;
export type VolumeMotion=typeof VOLUME_MOTIONS[number];
export interface AudioVolumeDirection { audioGainDb?:number; volumeMotion?:VolumeMotion; audioVolumeGroup?:string }
export interface AudioVolumeEffect { gainDb:number; motion:VolumeMotion; group:string; from:number; to:number }
export function actualVolume(direction?:AudioVolumeDirection) {
  if(direction?.audioGainDb===undefined&&!direction?.volumeMotion)return undefined;
  const gainDb=direction.audioGainDb??0,motion=direction.volumeMotion??'Consistent';
  if(!(AUDIO_GAIN_LEVELS as readonly number[]).includes(gainDb)||!(VOLUME_MOTIONS as readonly string[]).includes(motion))throw Object.assign(new Error('Invalid volume setting.'),{retryable:false});
  return {gainDb,motion};
}
export function volumeFactor(motion:VolumeMotion,phase:number) {
  const p=Math.max(0,Math.min(1,phase));
  return motion==='Rising'?.2+.8*p:motion==='Falling'?1-.8*p:motion==='Varied'?.65+.35*Math.cos(2*Math.PI*p):1;
}
export function processAudioVolume(buffer:AudioBuffer,effect?:AudioVolumeEffect):AudioBuffer {
  if(!effect)return buffer;
  actualVolume({audioGainDb:effect.gainDb,volumeMotion:effect.motion});
  if(!Number.isFinite(effect.from)||!Number.isFinite(effect.to)||effect.from<0||effect.to>1||effect.to<effect.from)throw Object.assign(new Error('Invalid volume progression.'),{retryable:false});
  if(effect.gainDb===0&&effect.motion==='Consistent')return buffer;
  let output:AudioBuffer;
  try{output=new AudioBuffer({numberOfChannels:buffer.numberOfChannels,length:buffer.length,sampleRate:buffer.sampleRate});}
  catch{throw Object.assign(new Error('This browser could not apply the volume adjustment. The original recording is unchanged.'),{retryable:false});}
  const gain=10**(effect.gainDb/20);
  for(let channel=0;channel<buffer.numberOfChannels;channel++){
    const input=buffer.getChannelData(channel),samples=output.getChannelData(channel);
    for(let i=0;i<input.length;i++){
      const phase=effect.from+(effect.to-effect.from)*i/Math.max(1,input.length-1);
      const value=input[i]*gain*volumeFactor(effect.motion,phase),level=Math.abs(value);
      // A smooth peak limiter, not per-clip normalization: fades remain fades.
      samples[i]=!Number.isFinite(value)?0:level<=.95?value:Math.sign(value)*(.95+.05*Math.tanh((level-.95)/.05));
    }
  }
  return output;
}
